import debounce from 'p-debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getProjects, JiraProject } from '../services/jira';

const BATCH_SIZE = 10;
const debouncedGetProjects = debounce(getProjects, 500);

export const useProjects = ({
  page = 0,
  query = '',
  onError,
}: {
  page: number;
  query: string;
  onError: VoidFunction;
}) => {
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(page);
  const [total, setTotal] = useState<number>(0);
  const pages = useMemo(
    () =>
      Array.from(
        { length: Math.ceil(total / BATCH_SIZE) },
        (_, index) => index + 1
      ),
    [BATCH_SIZE, total]
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [query]);

  const getPage = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        const { values, total } = await debouncedGetProjects({
          startAt: page * BATCH_SIZE,
          maxResults: BATCH_SIZE,
          query,
        });
        setProjects(values || []);
        setCurrentPage(page);
        setTotal(total);
      } catch (error) {
        onError();
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [BATCH_SIZE, query]
  );

  useEffect(() => {
    void getPage(page);
  }, [page, query]);

  return { projects, loading, getPage, currentPage, pages };
};
